import React from "react";
import { useDispatch, useSelector } from "react-redux";
import empty from "is-empty";

import { TxSelectors } from "src/redux/Tx/selectors";
import { TxActions } from "src/redux/Tx/actions";
import RadioButtons from "src/components/common/RadioButton/index";
import TextBox from "src/components/common/TextBox";
import { COLLAT_TYPES, LOAN_TYPES } from "src/redux/Tx/constants";
import { SUPPORTED_CURRENCIES } from "src/redux/MarketData/constants";
import CollapsibleMenu from "src/components/common/CollapsibleMenu";
import ReferenceRate from "src/components/views/PreviewActions/ReferenceRate";
import { VIEWS } from "src/redux/TopBar/constants";
import { AvailabilitySelectors } from "src/redux/Availability/selectors";
import { AvailabilityActions } from "src/redux/Availability/actions";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import Maturity from "src/components/views/PreviewActions/Maturity";
import Intermediary from "src/components/views/PreviewActions/Intermediary";
import FundCode from "src/components/views/PreviewActions/FundCode";
import TxType from "src/components/views/PreviewActions/TxType";

import styles from "./styles.module.scss";

const CALLABLE_OPTIONS = [
    { label: "Either", value: "" },
    { label: "Non-Call (N)", value: false },
    { label: "Call (Y)", value: true },
];

const COLLAT_OPTIONS = [
    { label: "Any", value: "" },
    { label: "Cash", value: COLLAT_TYPES.CA },
    { label: "Non Cash", value: COLLAT_TYPES.NC },
    { label: "Cash Pool", value: COLLAT_TYPES.CP },
];

const LOAN_TYPE_OPTIONS = [
    { label: "Regular", value: LOAN_TYPES.LOAN },
    { label: "EA", value: LOAN_TYPES.HOLD },
];

const isCcyValid = (ccy) => SUPPORTED_CURRENCIES.includes(ccy);

export default ({ type, isPortfolioTx = false }) => {
    const dispatch = useDispatch();
    const requestSettings = useSelector(TxSelectors.TxInitRequestSettings);
    const selectedCptys = useSelector(TxSelectors.TxInitSelectedCptys);
    const {
        "enable-90-day-max-maturity-date": enable90DayMaxMaturity,
        // Spec §3 "Loan Type = EA: Disabled". Filter the EA option out
        // of the loan-type radio under limited-ui so the user can't
        // pick it. Backend TxController.createPreLoan also rejects
        // non-LOAN rows under limited-ui; this keeps the radio in sync.
        "limited-ui": limitedUi,
    } = useSelector(SettingsSelectors.FeatureFlags);
    const loanTypeOptions = limitedUi
        ? LOAN_TYPE_OPTIONS.filter((o) => o.value === LOAN_TYPES.LOAN)
        : LOAN_TYPE_OPTIONS;

    const hasAccess =
        type === VIEWS.AVAILABILITY
            ? useSelector(AvailabilitySelectors.HasMarkitAccess)
            : useSelector(TxSelectors.HasMarkitAccess);

    const {
        callable,
        loanType,
        collateralType,
        error,
        maturityDate,
        intermediary,
        txType,
    } = requestSettings;
    const ccyList = collateralType.ccy;

    const updateSettings = (field) => (value) => {
        let newSettings = { ...requestSettings };

        if (field === "callable") {
            newSettings[field] = value;
        }

        if (field === "loanType") {
            newSettings[field] = value;
        }

        if (field === "collateralType") {
            newSettings[field].type = value;
        }
        if (field === "collateralCcy") {
            newSettings.collateralType.ccy = [...value];

            if (!value.every(isCcyValid))
                newSettings.error = "This currency is not supported";
            else newSettings.error = "";
        }

        dispatch(TxActions.SetRequestSettings(newSettings));
    };

    const applyRefRate = (value) => {
        return type === VIEWS.AVAILABILITY
            ? dispatch(AvailabilityActions.ApplyBenchmarkFee(value))
            : dispatch(TxActions.ApplyBenchmarkFee(value));
    };

    const applyMaturity = (value) => {
        return type === VIEWS.AVAILABILITY
            ? dispatch(
                  AvailabilityActions.ApplyMaturity(
                      value,
                      enable90DayMaxMaturity
                  )
              )
            : dispatch(TxActions.ApplyMaturity(value, enable90DayMaxMaturity));
    };

    const applyTxType = (txType) => {
        return dispatch(TxActions.ApplyTxType(txType));
    };

    const applyIntermediary = (value) => {
        return dispatch(TxActions.ApplyIntermediary(value));
    };

    return (
        <React.Fragment>
            {type !== VIEWS.AVAILABILITY && !isPortfolioTx && (
                <CollapsibleMenu
                    title="Loan / Exclusive Arrangement"
                    dataTestId="tx-init-loan-type"
                >
                    <Section className={styles.tuplet}>
                        <RadioButtons
                            options={loanTypeOptions}
                            dataTestId="tx-init-loan-type-radio"
                            value={loanType}
                            setter={updateSettings("loanType")}
                            checkIsDisabled={(opt) =>
                                opt.label == "EA" &&
                                selectedCptys.length > 0 &&
                                selectedCptys.some(
                                    // `?.` on both — TxInitSelectedCptys can
                                    // contain undefined slots when an id in
                                    // state hasn't yet resolved to a cpty
                                    // object in the directory state.
                                    (cpty) => cpty?.unregistered || cpty?.partner
                                )
                            }
                        />
                    </Section>
                </CollapsibleMenu>
            )}
            <FundCode />

            <ReferenceRate hasAccess={hasAccess} apply={applyRefRate} />

            <Maturity maturity={maturityDate} onChange={applyMaturity} />

            <CollapsibleMenu
                title="Request Criteria"
                dataTestId="tx-init-request-settings"
            >
                {type !== VIEWS.AVAILABILITY && (
                    <>
                        <TxType
                            txType={txType}
                            intermediary={intermediary}
                            onChange={applyTxType}
                        />
                        <div className={styles.splitter} />
                        <Intermediary
                            intermediary={intermediary}
                            txType={txType}
                            onChange={applyIntermediary}
                        />
                        <div className={styles.splitter} />
                    </>
                )}
                <Section className={styles.triplet}>
                    {<div className={styles.title}>Callable</div>}
                    <RadioButtons
                        options={CALLABLE_OPTIONS}
                        dataTestId="tx-request-select-callable"
                        value={callable}
                        setter={updateSettings("callable")}
                    />
                </Section>
                <div className={styles.splitter} />
                <Section className={styles.tuplet}>
                    {<div className={styles.title}>Collateral</div>}
                    <RadioButtons
                        options={COLLAT_OPTIONS}
                        dataTestId="tx-request-select-collat-type"
                        value={collateralType.type}
                        setter={updateSettings("collateralType")}
                    />
                </Section>
                <Section className={styles.currencySelect}>
                    <TextBox
                        id="tx-request-collat-ccy"
                        placeholder={"Type in currency codes"}
                        data={ccyList}
                        onChange={updateSettings("collateralCcy")}
                        tabIndex={0}
                        verifyData={isCcyValid}
                        showOrder
                        forceUpperCase
                        ariaLabel="Transaction collat-ccy"
                    />

                    {!empty(error) && (
                        <div className={styles.error}>{error}</div>
                    )}
                </Section>
            </CollapsibleMenu>
        </React.Fragment>
    );
};

const Section = ({ className, children }) => (
    <div className={[styles.section].concat(className || []).join(" ")}>
        <div className={styles.input}>{children}</div>
    </div>
);
